/*
  Lógica pura do portal "API do NINA": lê o documento OpenAPI 3 de um módulo (gerado pelo Swashbuckle
  no backend) e entrega estruturas prontas para a tela. Sem React, sem rede.

  Extensões do NINA por operação: `x-nina-permissoes` (string[]), `x-nina-anonimo` (bool) e
  `x-nina-restricoes` (string[], frases em português prontas para exibir; ausente = []).
*/

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete" | "head" | "options";

const METHODS: HttpMethod[] = ["get", "post", "put", "patch", "delete", "head", "options"];

/** Profundidade máxima da árvore de campos (design §4). */
export const MAX_DEPTH = 6;

export interface OpenApiDoc {
  openapi?: string;
  info?: { title?: string; description?: string; version?: string };
  paths?: Record<string, Record<string, any>>;
  components?: { schemas?: Record<string, any>; parameters?: Record<string, any>; requestBodies?: Record<string, any>; responses?: Record<string, any> };
}

export interface ModuloInfo {
  id: string;
  nome: string;
  descricao: string;
  endpoints: number;
}

export interface FieldNode {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
  /** Esquema que se referencia a si mesmo (direta ou indiretamente): não é expandido de novo. */
  ciclo?: boolean;
  /** Passou de MAX_DEPTH: há mais campos abaixo que não foram expandidos. */
  truncado?: boolean;
  children: FieldNode[];
}

export interface ParameterInfo {
  name: string;
  in: string;
  type: string;
  required: boolean;
  description?: string;
}

export interface BodyInfo {
  contentType: string;
  fields: FieldNode[];
  /** Tipo do corpo (ex.: Ticket, Ticket[], string); útil quando não há campos para listar. */
  type: string;
}

export interface ResponseInfo {
  status: string;
  description?: string;
  body?: BodyInfo;
}

export interface Operation {
  key: string;
  method: HttpMethod;
  path: string;
  tag: string;
  summary: string;
  description: string;
  permissoes: string[];
  /** Restrições de negócio da operação (ex.: limites do plano), já em texto para exibir. */
  restricoes: string[];
  anonimo: boolean;
  operation: any;
  pathItem: any;
}

export interface TagGroup {
  tag: string;
  operations: Operation[];
}

const SEM_TAG = "Sem grupo";

export const refName = (ref: string): string => ref.split("/").pop() ?? ref;

/** Resolve `#/components/...`. Devolve undefined se o alvo não existir. */
export function resolveRef(doc: OpenApiDoc, ref: string): any | undefined {
  if (!ref.startsWith("#/")) return undefined;
  let atual: any = doc;
  for (const parte of ref.slice(2).split("/")) {
    if (atual == null) return undefined;
    atual = atual[decodeURIComponent(parte.replace(/~1/g, "/").replace(/~0/g, "~"))];
  }
  return atual;
}

const deref = (doc: OpenApiDoc, obj: any): any =>
  obj && typeof obj.$ref === "string" ? resolveRef(doc, obj.$ref) ?? {} : obj ?? {};

/** Todas as operações do documento, na ordem do documento. */
export function listOperations(doc: OpenApiDoc): Operation[] {
  const out: Operation[] = [];
  for (const [path, pathItem] of Object.entries(doc.paths ?? {})) {
    for (const method of METHODS) {
      const op = pathItem?.[method];
      if (!op) continue;
      const permissoes = Array.isArray(op["x-nina-permissoes"]) ? (op["x-nina-permissoes"] as string[]) : [];
      const restricoes = Array.isArray(op["x-nina-restricoes"])
        ? (op["x-nina-restricoes"] as unknown[]).filter((r): r is string => typeof r === "string" && r.trim() !== "")
        : [];
      out.push({
        key: `${method.toUpperCase()} ${path}`,
        method,
        path,
        tag: Array.isArray(op.tags) && op.tags.length > 0 ? String(op.tags[0]) : SEM_TAG,
        summary: op.summary ?? "",
        description: op.description ?? "",
        permissoes,
        restricoes,
        anonimo: op["x-nina-anonimo"] === true,
        operation: op,
        pathItem,
      });
    }
  }
  return out;
}

const normaliza = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Busca por texto na rota, no resumo, na descrição e no método. Vazio = tudo. */
export function filterOperations(ops: Operation[], busca: string): Operation[] {
  const termos = normaliza(busca).split(/\s+/).filter(Boolean);
  if (termos.length === 0) return ops;
  return ops.filter((o) => {
    const alvo = normaliza(`${o.method} ${o.path} ${o.summary} ${o.description}`);
    return termos.every((t) => alvo.includes(t));
  });
}

/** Agrupa por tag (controller), tags em ordem alfabética, operações na ordem do documento. */
export function groupByTag(ops: Operation[]): TagGroup[] {
  const mapa = new Map<string, Operation[]>();
  for (const o of ops) mapa.set(o.tag, [...(mapa.get(o.tag) ?? []), o]);
  return Array.from(mapa.entries())
    .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
    .map(([tag, operations]) => ({ tag, operations }));
}

/** Texto curto das permissões da operação para a tela. */
export function rotuloPermissoes(op: Pick<Operation, "permissoes" | "anonimo">): string {
  if (op.anonimo) return "Anônimo";
  if (op.permissoes.length === 0) return "Qualquer usuário autenticado";
  return op.permissoes.join(", ");
}

function tipoDe(doc: OpenApiDoc, schema: any): string {
  if (!schema) return "any";
  if (schema.$ref) return refName(schema.$ref);
  if (schema.type === "array") return `${tipoDe(doc, schema.items)}[]`;
  const base = schema.type ?? (schema.properties || schema.allOf || schema.oneOf || schema.anyOf ? "object" : "any");
  return schema.format ? `${base} (${schema.format})` : base;
}

/** Junta `allOf` num esquema só (propriedades e obrigatórios somados). */
function achata(doc: OpenApiDoc, schema: any, vistos: Set<string>): any {
  if (!Array.isArray(schema.allOf)) return schema;
  const juntado: any = { ...schema, properties: { ...(schema.properties ?? {}) }, required: [...(schema.required ?? [])] };
  delete juntado.allOf;
  for (const parte of schema.allOf) {
    let visitados = vistos;
    if (parte.$ref) {
      if (vistos.has(parte.$ref)) continue;
      visitados = new Set(vistos).add(parte.$ref);
    }
    const p = achata(doc, deref(doc, parte), visitados);
    Object.assign(juntado.properties, p.properties ?? {});
    juntado.required.push(...(p.required ?? []));
    juntado.type = juntado.type ?? p.type;
    juntado.description = juntado.description ?? p.description;
  }
  return juntado;
}

function noDe(doc: OpenApiDoc, nome: string, schemaBruto: any, required: boolean, pilha: string[], depth: number): FieldNode {
  const tipo = tipoDe(doc, schemaBruto);
  // `array` cujo item é objeto: os campos mostrados são os do item.
  const alvo = schemaBruto?.type === "array" && schemaBruto.items ? schemaBruto.items : schemaBruto ?? {};
  const ref: string | undefined = typeof alvo.$ref === "string" ? alvo.$ref : undefined;
  const schema = ref ? deref(doc, alvo) : alvo;

  const node: FieldNode = {
    name: nome,
    type: tipo,
    required,
    description: schemaBruto?.description ?? schema.description,
    children: [],
  };
  if (Array.isArray(schema.enum)) node.enumValues = schema.enum.map(String);

  if (ref && pilha.includes(ref)) {
    node.ciclo = true;
    return node;
  }
  const solido = achata(doc, schema, new Set(ref ? [ref] : []));
  const props = solido.properties ?? {};
  const variantes: any[] = [...(solido.oneOf ?? []), ...(solido.anyOf ?? [])];
  if (Object.keys(props).length === 0 && variantes.length === 0) return node;

  if (depth >= MAX_DEPTH) {
    node.truncado = true;
    return node;
  }
  const novaPilha = ref ? [...pilha, ref] : pilha;
  const obrigatorios: string[] = solido.required ?? [];
  node.children = Object.entries(props).map(([n, s]) => noDe(doc, n, s, obrigatorios.includes(n), novaPilha, depth + 1));
  variantes.forEach((v, i) => node.children.push(noDe(doc, `variante ${i + 1}`, v, false, novaPilha, depth + 1)));
  return node;
}

/** Árvore de campos de um esquema, resolvendo `$ref` com proteção contra ciclo e profundidade máxima. */
export function buildFieldTree(doc: OpenApiDoc, schema: any): FieldNode[] {
  if (!schema) return [];
  return noDe(doc, "", schema, false, [], 1).children;
}

function corpoDe(doc: OpenApiDoc, content: Record<string, any> | undefined): BodyInfo | undefined {
  if (!content) return undefined;
  const contentType = content["application/json"] ? "application/json" : Object.keys(content)[0];
  if (!contentType) return undefined;
  const schema = content[contentType]?.schema;
  if (!schema) return { contentType, fields: [], type: "any" };
  return { contentType, fields: buildFieldTree(doc, schema), type: tipoDe(doc, schema) };
}

/** Parâmetros da operação somados aos do caminho (a operação vence em caso de mesmo nome e local). */
export function getParameters(doc: OpenApiDoc, op: Operation): ParameterInfo[] {
  const todos = [...(op.pathItem?.parameters ?? []), ...(op.operation?.parameters ?? [])].map((p) => deref(doc, p));
  const porChave = new Map<string, ParameterInfo>();
  for (const p of todos) {
    if (!p.name) continue;
    porChave.set(`${p.in}:${p.name}`, {
      name: p.name,
      in: p.in ?? "query",
      type: tipoDe(doc, p.schema),
      required: p.required === true || p.in === "path",
      description: p.description,
    });
  }
  return Array.from(porChave.values());
}

export function getRequestBody(doc: OpenApiDoc, op: Operation): BodyInfo | undefined {
  const rb = op.operation?.requestBody;
  return rb ? corpoDe(doc, deref(doc, rb).content) : undefined;
}

export function getResponses(doc: OpenApiDoc, op: Operation): ResponseInfo[] {
  return Object.entries<any>(op.operation?.responses ?? {}).map(([status, r]) => {
    const resp = deref(doc, r);
    return { status, description: resp.description, body: corpoDe(doc, resp.content) };
  });
}
